// SearchLayoutComponent.jsx
'use strict';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import Redirect from '../container/Redirect.js';
import HomeSearchBar from '../container/HomeSearchBar.js';

import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';

import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import {
  color,
  headerHeight,
  footerNavigationHeight,
  searchSwaggerFilterHeight,
  iPhoneXSeriesFooterPaddingInPWA,
  searchBarHeight,
} from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import ResetHeadingStyle from '../style/ResetHeadingStyle.js';

import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import {
  searchType,
  searchSwaggerCategoryPath,
  searchPageTDKTranslateKeys,
} from '../resource/searchConstants.js';

import DropdownIconSrc from '../../img/btn-dropdown-white.svg';
import BackIconSource from '../../img/ic_topbar_back.svg';
import media from '../style/media.js';
import { generateLangPath, getLangFromPath } from '../resource/langPath.js';

const swaggerCategories = [
  searchSwaggerCategoryPath.ONLINE,
  searchSwaggerCategoryPath.NEW,
];
const tabsHeight = 48;

export class SearchLayoutComponent extends React.PureComponent {
  state = {
    isSwaggerFilterOpen: false,
    currentCategory: swaggerCategories[0],
  };
  nextTick = null;

  componentDidMount() {
    const { setSearch, query } = this.props;
    if (query) {
      setSearch({ query });
    }
  }

  componentWillUnmount() {
    const { setSearch } = this.props;
    clearTimeout(this.nextTick);
    // clear query string and search bar value
    setSearch({ query: '' });
  }

  getRedirectPathname = () => {
    const { type, category } = this.props;

    const typeToUpperCase = type?.toUpperCase();
    if (!searchType[typeToUpperCase]) {
      return `/search/${searchType.ALL}`;
    }
    const shouldFallbackSwaggerCategory =
      type === searchType.SWAGGER && !swaggerCategories.includes(category);
    if (shouldFallbackSwaggerCategory) {
      return `/search/${searchType.SWAGGER}/${swaggerCategories[0]}`;
    }
    return null;
  };
  handleTabChange = async (event, value) => {
    event?.preventDefault();
    const {
      replace,
      location: { search: locationSearch },
    } = this.props;
    const { currentCategory } = this.state;
    const search = new URLSearchParams(locationSearch);
    ['sorts', 'filters', 'feed'].forEach(key => search.delete(key));
    const searchString = search.toString() ? `?${search.toString()}` : '';

    let basePath = `/search/${value}`;
    if (value === searchType.SWAGGER) {
      basePath = `/search/${value}/${currentCategory}`;
    }
    await replace(basePath + searchString);

    this.nextTick = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  };
  toggleSwaggerFilter = () => {
    const { isSwaggerFilterOpen } = this.state;
    this.setState({
      isSwaggerFilterOpen: !isSwaggerFilterOpen,
    });
  };
  handleSwaggerFilterOnChange = ({ newCategory }) => {
    const { replace, setSearch, category } = this.props;
    if (category !== newCategory) {
      setSearch({ category: newCategory });
    }
    this.setState({
      isSwaggerFilterOpen: false,
      currentCategory: newCategory,
    });
    const newPath = `/search/${searchType.SWAGGER}/${newCategory}`;
    replace(newPath);
  };
  renderUserFilter = () => {
    const { t, category: currentCategory } = this.props;
    const { isSwaggerFilterOpen } = this.state;
    const currentFilterText =
      currentCategory === searchSwaggerCategoryPath.ONLINE
        ? t('sort_recently_online', {
            ns: TranslationNamespace.SEARCH,
          })
        : t('new', {
            ns: TranslationNamespace.SEARCH,
          });
    return (
      <UserCategoryDropdown>
        <UserCategoryButton onClick={this.toggleSwaggerFilter}>
          <UserCategoryTitle>{currentFilterText}</UserCategoryTitle>
          <DropdownIcon
            src={DropdownIconSrc}
            alt="Dropdown"
            isOpen={isSwaggerFilterOpen}
          />
        </UserCategoryButton>
        <UserCategoryDropdownList isOpen={isSwaggerFilterOpen}>
          {swaggerCategories.map(category => (
            <UserCategoryOption
              key={category}
              onClick={() => {
                this.handleSwaggerFilterOnChange({
                  newCategory: category,
                });
              }}
              isCurrent={currentCategory === category}
            >
              {category === searchSwaggerCategoryPath.ONLINE
                ? t('sort_recently_online', {
                    ns: TranslationNamespace.SEARCH,
                  })
                : t('new', {
                    ns: TranslationNamespace.SEARCH,
                  })}
            </UserCategoryOption>
          ))}
        </UserCategoryDropdownList>
      </UserCategoryDropdown>
    );
  };

  getTabHref = tabKey => {
    const {
      location: { search: locationSearch, pathname },
    } = this.props;
    const { currentCategory } = this.state;
    const currentLang = getLangFromPath(pathname);
    const search = new URLSearchParams(locationSearch);
    ['sorts', 'filters', 'feed'].forEach(key => search.delete(key));
    const searchString = search.toString() ? `?${search.toString()}` : '';

    let basePath = `/search/${tabKey}`;
    if (tabKey === searchType.SWAGGER) {
      basePath = `/search/${tabKey}/${currentCategory}`;
    }

    return generateLangPath(basePath, currentLang) + searchString;
  };

  getSeoTDK = ({ t, type, query }) => {
    const translateKeys =
      searchPageTDKTranslateKeys[type] || searchPageTDKTranslateKeys.default;
    const queryPrefix = query ? `${query} - ` : '';
    const title = `${queryPrefix}${t(translateKeys.title, {
      ns: TranslationNamespace.SEARCH,
    })}`;
    const description = `${queryPrefix}${t(translateKeys.description, {
      ns: TranslationNamespace.SEARCH,
    })}`;
    const h1 = `${queryPrefix}${t(
      translateKeys.h1 || translateKeys.description,
      {
        ns: TranslationNamespace.SEARCH,
      }
    )}`;
    return { title, description, h1 };
  };

  render() {
    const {
      t,
      goBack,
      query,
      type,
      location: { search },
      isOnMobile,
      children,
    } = this.props;

    const redirectPathname = this.getRedirectPathname();
    if (redirectPathname) {
      return <Redirect to={{ pathname: redirectPathname, search }} />;
    }

    const seoTDK = this.getSeoTDK({ t, type, query });
    return (
      <StyledSearch
        isIphoneXSeriesPWA={getIsIphoneXSeriesPWA()}
        hasSwaggerFilter={type === searchType.SWAGGER && !query}
      >
        <HeadingOne>{seoTDK.h1}</HeadingOne>
        <SearchContainer>
          <Header>
            <SearchBarWrapper>
              <BackButton
                data-element_id={ButtonId.All.ButtonBack}
                onClick={goBack}
              >
                <img
                  src={BackIconSource}
                  alt={t('general_cancel', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                />
              </BackButton>
              <HomeSearchBar />
            </SearchBarWrapper>
            <TabsWrapper>
              <Tabs
                value={type}
                onChange={this.handleTabChange}
                isScrollable
                inline={!isOnMobile}
              >
                <Tab
                  data-element_id={ButtonId.Tab.TabSearchAll}
                  value={searchType.ALL}
                  label={t('tab_all', {
                    ns: TranslationNamespace.SEARCH,
                  })}
                  href={this.getTabHref(searchType.ALL)}
                />
                <Tab
                  data-element_id={ButtonId.Tab.TabSearchVideo}
                  value={searchType.FLIX}
                  label={t('title_flix', {
                    ns: TranslationNamespace.SEARCH,
                  })}
                  href={this.getTabHref(searchType.FLIX)}
                />
                <Tab
                  data-element_id={ButtonId.Tab.TabSearchCreator}
                  value={searchType.SWAGGER}
                  label={t('search_title_swagger', {
                    ns: TranslationNamespace.SEARCH,
                  })}
                  href={this.getTabHref(searchType.SWAGGER)}
                />
                <Tab
                  data-element_id={ButtonId.Tab.TabSearchStory}
                  value={searchType.STORY}
                  label={t('broadcast', {
                    ns: TranslationNamespace.SEARCH,
                  })}
                  href={this.getTabHref(searchType.STORY)}
                />
              </Tabs>
              {type === searchType.SWAGGER && !query && this.renderUserFilter()}
            </TabsWrapper>
          </Header>
          {children}
        </SearchContainer>
      </StyledSearch>
    );
  }
}

SearchLayoutComponent.propTypes = {
  children: PropTypes.node,
  query: PropTypes.string,
  category: PropTypes.string,
  type: PropTypes.string,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }),
  replace: PropTypes.func,
  setSearch: PropTypes.func,
  goBack: PropTypes.func,
  t: PropTypes.func.isRequired,
  isOnMobile: PropTypes.bool,
};

SearchLayoutComponent.defaultProps = {
  children: null,
  staticContext: {},
  query: '',
  category: '',
  type: searchType.ALL,
  location: {},
  replace: () => null,
  setSearch: () => null,
  goBack: () => null,
  isOnMobile: false,
};

const TabsWrapper = styled.div`
  height: 48px;
  padding: 0 16px;
  border-bottom: 1px solid rgba(100, 100, 100, 0.25);
  background-color: ${color.black};

  ${media.mobile`
    height: 40px;
  `}
`;

const StyledSearch = styled.div`
  padding-top: calc(${headerHeight}px + var(--space-to-pinned-notification));
  padding-bottom: ${({ isIphoneXSeriesPWA }) =>
    isIphoneXSeriesPWA
      ? `${footerNavigationHeight + iPhoneXSeriesFooterPaddingInPWA}px`
      : `${footerNavigationHeight}px`};

  ${media.mobile`
    padding-top: ${({ hasSwaggerFilter }) => (hasSwaggerFilter ? `calc(${headerHeight + tabsHeight + searchSwaggerFilterHeight}px + var(--space-to-pinned-notification))` : `calc(${headerHeight + tabsHeight}px + var(--space-to-pinned-notification))`)};
  `};
`;

const SearchContainer = styled.div`
  margin: 0 auto;
  max-width: 962px;
  border-left: solid 1px ${color.grey[900]};
  border-right: solid 1px ${color.grey[900]};
  ${media.mobile`
    max-width: 100%;
    border: unset;
  `}
`;

const Header = styled.header`
  position: relative;
  width: 100%;
  height: ${searchBarHeight + tabsHeight}px;
  z-index: 3;

  ${media.mobile`
    position: fixed;
    top: var(--space-to-pinned-notification);
    left: 0;
    height: 104px;
  `}
`;

const HeadingOne = styled.h1`
  ${ResetHeadingStyle}
  font-size: 14px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;
  visibility: hidden;
`;

const SearchBarWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 16px 20px;
  background-color: ${color.black};
  ${media.tablet`
      padding: 12px 16px;
  `}
  ${media.mobile`
      padding: 8px 16px;
  `}
`;

const BackButton = styled.button`
  ${ResetButtonStyle};
  margin-right: 12px;
  height: 32px;
  width: 32px;
  flex: none;
  > img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }

  display: none;
  ${media.mobile`
    display: block;
  `}
`;

const UserCategoryDropdown = styled.div`
  position: relative;
  margin: 0 -16px;
  height: ${searchSwaggerFilterHeight}px;
`;

const DropdownIcon = styled.img`
  width: 16px;
  height: 16px;
  transform: rotate(${({ isOpen }) => (isOpen ? -180 : 0)}deg);
  transition: transform 0.3s linear;
`;

const UserCategoryDropdownList = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  left: 0;
  z-index: 1;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow: hidden;
  background-color: #191919;
  transition: max-height 0.3s;
  max-height: ${({ isOpen }) => (isOpen ? 72 : 0)}px;
`;

const UserCategoryOption = styled.a`
  display: block;
  height: 36px;
  width: 100%;
  border-top: 1px solid rgba(100, 100, 100, 0.5);
  text-align: center;
  font-size: 14px;
  line-height: 36px;
  color: ${({ isCurrent }) => (isCurrent ? '#ffffff' : '#646464')};
  transition: all 0.3s linear;
  &:last-child {
    border-bottom: 1px solid rgba(100, 100, 100, 0.5);
  }
  cursor: pointer;
`;

const UserCategoryButton = styled.button`
  ${ResetButtonStyle};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${searchSwaggerFilterHeight}px;
  width: 100%;
  font-size: 14px;
  color: #ffffff;
`;

const UserCategoryTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
`;

export default withTranslation()(SearchLayoutComponent);
